import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

import Home from "./pages/Home";
import Login from "./pages/Login";
import StudentLogin from "./pages/StudentLogin";
import Warden from "./pages/Warden";
import Unauthorized from "./pages/Unauthorized";
import Navbar from "./components/Navbar";
import Loader from "./components/Loader";

// Layout Wrapper to conditionally show Navbar
function Layout({ children }) {
  const location = useLocation();

  const hideNavbarRoutes = ["/login", "/student-login", "/warden"];
  const shouldHideNavbar = hideNavbarRoutes.includes(location.pathname);

  return (
    <>
      {!shouldHideNavbar && <Navbar />}
      {children}
    </>
  );
}

// Guard component for sso-callback
function SsoCallbackGuard() {
  const location = useLocation();

  // Check the exact query string
  if (
    location.search ===
    "?redirect_url=http%3A%2F%2Flocalhost%3A3000%2Fwarden"
  ) {
    return <Unauthorized />;
  }

  // If query is different, redirect to login
  return <Login />;
}

export default function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize AOS animation library
    AOS.init({ 
      duration: 1000,
      once: true, // Animation happens only once
      mirror: false, // Elements don't animate out while scrolling past them
    });

    // Loading timeout
    const timeout = setTimeout(() => setLoading(false), 1200);

    // Disable Right-Click
    const disableRightClick = (e) => {
      e.preventDefault();
      return false;
    };
    document.addEventListener("contextmenu", disableRightClick);

    // Disable Inspect Shortcuts (Ctrl+U, Ctrl+Shift+I, F12, etc.)
    const disableShortcuts = (e) => {
      // Ctrl+U (View Source)
      if (e.ctrlKey && e.key.toLowerCase() === "u") {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+I (Inspect)
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "i") {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+C (Inspect Element)
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "j") {
        e.preventDefault();
        return false;
      }
      // F12 (DevTools)
      if (e.key === "F12") {
        e.preventDefault();
        return false;
      }
      // Ctrl+S (Save Page)
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        return false;
      }
    };
    document.addEventListener("keydown", disableShortcuts);

    // Disable text selection (optional - uncomment if needed)
    // document.body.style.userSelect = 'none';
    // document.body.style.webkitUserSelect = 'none';
    // document.body.style.mozUserSelect = 'none';
    // document.body.style.msUserSelect = 'none';

    // Cleanup function
    return () => {
      clearTimeout(timeout);
      document.removeEventListener("contextmenu", disableRightClick);
      document.removeEventListener("keydown", disableShortcuts);
      // document.body.style.userSelect = 'auto'; // Reset if you enabled the above
    };
  }, []);

  // Show loader while loading
  if (loading) return <Loader />;

  return (
    <Router>
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/student-login" element={<StudentLogin />} />
          <Route path="/warden" element={<Warden />} />

          {/* Special check for /login/sso-callback */}
          <Route
            path="/login/sso-callback"
            element={<SsoCallbackGuard />}
          />

          {/* Unauthorized page */}
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* 404 Catch-all route (optional) */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </Router>
  );
}

// Optional: 404 Not Found component
function NotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="text-center">
        <h1 className="text-6xl sm:text-8xl font-bold text-maroon mb-4">404</h1>
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-4">
          Page Not Found
        </h2>
        <p className="text-gray-600 mb-8 text-sm sm:text-base">
          The page you're looking for doesn't exist.
        </p>
        <a
          href="/"
          className="inline-block bg-maroon text-white px-6 py-3 rounded-lg font-medium hover:bg-maroonDark transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95"
        >
          Go Back Home
        </a>
      </div>
    </div>
  );
}